import { FunctionComponent, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { classNames } from "@/utils/classNames";
import { Toggle } from "@/components/Toggle/Toggle";

import { Tier } from "./criteriaTiers";

interface CriterionCardProps {
	index: number;
	title: string;
	hint: string;
	/** Small pill after the title (e.g. "AI agent"). */
	badge?: string;
	active: boolean;
	name: string;
	onToggle: (active: boolean) => void;
	children: ReactNode;
}

/**
 * One grading criterion: what it asks, and its four graded buckets — collapsed
 * to a single line while it is switched off, so an inactive criterion cannot be
 * mistaken for one that scores nothing.
 */
export const CriterionCard: FunctionComponent<CriterionCardProps> = ({
	index,
	title,
	hint,
	badge,
	active,
	name,
	onToggle,
	children,
}) => {
	const { t } = useTranslation();

	return (
		<div
			className={classNames(
				"rounded-xl border border-border-light p-4",
				active ? "bg-white" : "bg-background-light"
			)}
		>
			<div className="flex items-start gap-3">
				<div className="min-w-0 flex-1">
					<h3
						className={classNames(
							"flex items-center gap-2 text-sm font-bold",
							active ? "text-text" : "text-text-meta"
						)}
					>
						<span
							aria-hidden="true"
							className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-action-200 text-[11px] text-brand-light"
						>
							{index}
						</span>
						{title}
						{badge && (
							<span className="rounded-full bg-blue-action-200 px-2 py-0.5 text-[10px] font-semibold text-brand-light">
								{badge}
							</span>
						)}
					</h3>
					<p className="ml-7 mt-0.5 text-xs leading-relaxed text-text-meta">{hint}</p>
				</div>
				<Toggle isChecked={active} name={name} onChange={() => onToggle(!active)} />
			</div>

			{active ? (
				<div className="ml-7 mt-3 flex flex-col gap-2">{children}</div>
			) : (
				<p className="ml-7 mt-2 text-xs text-text-meta">
					{t("onboardingTour.scoringStep.grading.switchedOff")}
				</p>
			)}
		</div>
	);
};

const TIER_STYLE: Record<Tier, { dot: string; label: string; chip: string }> = {
	3: {
		dot: "bg-status-success",
		label: "text-status-success",
		chip: "bg-status-success-light text-status-success",
	},
	2: {
		dot: "bg-status-success-light",
		label: "text-status-success",
		chip: "bg-status-success-light text-status-success",
	},
	1: {
		dot: "bg-status-warning",
		label: "text-status-warning",
		chip: "bg-status-warning-light text-status-warning",
	},
	0: {
		dot: "bg-status-error",
		label: "text-status-error",
		chip: "bg-status-error-light text-status-error",
	},
};

export const tierChipClassName = (tier: Tier): string => TIER_STYLE[tier].chip;

interface TierRowProps {
	tier: Tier;
	/** Overrides the "+3 points" style label (the Strategic Fit card reads differently). */
	label?: string;
	children: ReactNode;
}

/** One graded bucket of a criterion: its label and whatever edits it. */
export const TierRow: FunctionComponent<TierRowProps> = ({ tier, label, children }) => {
	const { t } = useTranslation();
	const style = TIER_STYLE[tier];

	return (
		<div className="flex items-start gap-3">
			<span
				className={classNames(
					"flex w-[104px] shrink-0 items-center gap-1.5 pt-2 text-xs font-bold",
					style.label
				)}
			>
				<span className={classNames("h-2 w-2 shrink-0 rounded-full", style.dot)} />
				{label ?? t(`onboardingTour.scoringStep.grading.tiers.${tier}`)}
			</span>
			{children}
		</div>
	);
};
